import { useState, useMemo } from 'react';
import { Download, RefreshCw, Eraser } from 'lucide-react';

/**
 * Escape a value for CSV (wrap in quotes if contains comma, newline, or quote).
 */
function csvEscape(val) {
  const s = String(val ?? '');
  if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Sanitize Oracle error for display/export: avoid misleading "timeEvents API" (NICE uses timeRecordEventRequests).
 */
function sanitizeOracleMessage(msg) {
  if (msg == null || typeof msg !== 'string') return msg;
  return msg.replace(/timeEvents API/gi, 'timeRecordEventRequests and child endpoints');
}

function buildErrorLogCsv(results) {
  const failed = results.filter((r) => r.status === 'Failed');
  const headers = ['Agent_No', 'Person ID', 'Status', 'Exception', 'Oracle Message'];
  const rows = failed.map((r) => [
    r.Agent_No ?? r.agent ?? '',
    r.PersonHCM ?? r.personId ?? '',
    r.status ?? 'Failed',
    r.exception ?? '',
    sanitizeOracleMessage((r.error ?? '').replace(/\r?\n/g, ' ')),
  ]);
  const lines = [headers.map(csvEscape).join(',')].concat(
    rows.map((row) => row.map(csvEscape).join(','))
  );
  return lines.join('\r\n');
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const UploadStatus = ({ results: resultsProp, result, apiBaseUrl, uploadApiUrl, onRefreshJobStatus, onClearProcessLogs }) => {
  const [filter, setFilter] = useState('All');
  const results = useMemo(
    () => resultsProp ?? result?.results ?? [],
    [resultsProp, result?.results]
  );
  const jobStatus = result?.status || '';
  const isJobView = Boolean(result?.jobId);
  const isJobInProgress = ['queued', 'processing'].includes(jobStatus);

  const totalRows = result?.totalRows ?? results.length;
  const successfulCount = result?.successful ?? results.filter((r) => r.status === 'Success').length;
  const failedCount = result?.failed ?? results.filter((r) => r.status === 'Failed').length;

  const filteredResults = useMemo(() => {
    if (filter === 'Success') return results.filter((r) => r.status === 'Success');
    if (filter === 'Failed') return results.filter((r) => r.status === 'Failed');
    return results;
  }, [results, filter]);

  const handleDownloadErrorLog = () => {
    const csv = buildErrorLogCsv(results);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    downloadBlob(blob, `upload-error-log-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const oracleBody = result?.oracleResponseBody ?? (result?._html404 ? result._rawBody : null);

  if (!results.length && !isJobView) return null;

  return (
    <div className="card-panel overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
        <h2 className="text-lg font-semibold text-slate-800">Process logs</h2>
        {typeof onClearProcessLogs === 'function' && (
          <button
            type="button"
            onClick={onClearProcessLogs}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-900"
          >
            <Eraser className="h-4 w-4 shrink-0" />
            Clear process logs
          </button>
        )}
      </div>

      {isJobView && (
        <div className="mx-4 mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">Job {result.jobId}</p>
              <p className="mt-1 text-sm text-slate-700">
                Status: <strong>{result.status}</strong>
                {result.stage ? <> · Stage: <strong>{result.stage}</strong></> : null}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                File: {result.fileName || '--'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {onRefreshJobStatus && (
                <button
                  type="button"
                  onClick={onRefreshJobStatus}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh status
                </button>
              )}
              {result?.files?.archive && (
                <a
                  href={`${apiBaseUrl || ''}${result.files.archive}`}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                >
                  <Download className="h-4 w-4" />
                  Archive
                </a>
              )}
              {result?.files?.log && (
                <a
                  href={`${apiBaseUrl || ''}${result.files.log}`}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                >
                  <Download className="h-4 w-4" />
                  Log
                </a>
              )}
              {result?.files?.error && (
                <a
                  href={`${apiBaseUrl || ''}${result.files.error}`}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                >
                  <Download className="h-4 w-4" />
                  Error file
                </a>
              )}
            </div>
          </div>
          <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className={`h-full rounded-full bg-cyan-600 transition-all duration-300 ${isJobInProgress ? 'animate-pulse' : ''}`}
              style={{ width: `${Math.max(0, Math.min(100, result.progressPercent ?? 0))}%` }}
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
            <span>Progress: {result.progressPercent ?? 0}%</span>
            <span>Total rows: {result.totalRows ?? 0}</span>
            <span>Processed: {result.processedRows ?? 0}</span>
            <span>Successful: {result.successful ?? 0}</span>
            <span>Failed: {result.failed ?? 0}</span>
          </div>
          {result.message &&
            !(result.status === 'completed' && (result.failed ?? 0) === 0) && (
              <p className="mt-3 text-sm text-slate-700 whitespace-pre-line">{result.message}</p>
            )}
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 mx-4 mt-4 mb-6">
        <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
          <p className="text-sm font-medium text-slate-600">Total rows</p>
          <p className="text-2xl font-bold text-slate-800">{totalRows}</p>
        </div>
        <div className="p-4 rounded-lg border border-slate-200 bg-emerald-50/50">
          <p className="text-sm font-medium text-slate-600">Successful</p>
          <p className="text-2xl font-bold text-emerald-700">{successfulCount}</p>
        </div>
        <div className="p-4 rounded-lg border border-slate-200 bg-red-50/50">
          <p className="text-sm font-medium text-slate-600">Failed</p>
          <p className="text-2xl font-bold text-red-700">{failedCount}</p>
        </div>
      </div>

      {results.length > 0 && results.some((r) => r.exception) && (
        <div className="mx-4 mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
          <strong>Missing punch:</strong> {results.filter((r) => r.exception).length} row(s) have a missing in or out punch. A comment is sent to Oracle for each (e.g. &quot;Missing out punch&quot;). In Time and Labor, click the <strong>comment icon</strong> on that row to view the comment.
        </div>
      )}

      {/* Filter + Export */}
      {results.length > 0 && (
      <div className="flex flex-wrap items-center justify-between gap-4 mx-4 mb-4">
        <div className="flex items-center gap-2">
          <label htmlFor="upload-status-filter" className="text-sm font-medium text-slate-700">
            Show:
          </label>
          <select
            id="upload-status-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
          >
            <option value="All">All rows</option>
            <option value="Success">Success only</option>
            <option value="Failed">Failed only</option>
          </select>
        </div>
        {failedCount > 0 && (
          <button
            type="button"
            onClick={handleDownloadErrorLog}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <Download className="h-4 w-4" />
            Download error log
          </button>
        )}
      </div>
      )}

      {/* 2. Detailed Log Table */}
      {results.length > 0 && (
      <div className="overflow-x-auto max-h-[400px] overflow-y-auto border-t border-slate-200">
        <table className="min-w-full text-sm text-left divide-y divide-slate-200">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="p-3 font-semibold text-slate-700">Agent No</th>
              <th className="p-3 font-semibold text-slate-700">Person ID</th>
              <th className="p-3 font-semibold text-slate-700">Status</th>
              <th className="p-3 font-semibold text-slate-700">Exception</th>
              <th className="p-3 font-semibold text-slate-700">Oracle message</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {filteredResults.map((res, index) => (
              <tr
                key={index}
                className={res.status === 'Failed' ? 'bg-red-50/50' : res.exception ? 'bg-amber-50/50' : 'hover:bg-slate-50'}
                title={res.status === 'Failed' && res.error ? res.error : res.exception || undefined}
              >
                <td className="p-3 font-medium text-slate-800">{res.Agent_No ?? res.agent}</td>
                <td className="p-3 text-slate-700">{res.PersonHCM ?? res.personId ?? '--'}</td>
                <td className="p-3">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      res.status === 'Success' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {res.status}
                  </span>
                </td>
                <td className="p-3 text-amber-700 italic">{res.exception ?? '--'}</td>
                <td className="p-3 text-red-600 text-sm" title={res.error}>
                  {res.status === 'Failed' ? (res.error ?? '--') : '--'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};

export default UploadStatus;
